// opencv-loader.js - OpenCV.js lazy loading

let state = 'idle'; // idle | loading | ready | error
let loadPromise = null;

/**
 * OpenCV.js 로드 상태 확인
 * @returns {boolean}
 */
export function isOpenCVReady() {
  return state === 'ready';
}

/**
 * OpenCV.js 로드 상태 반환
 * @returns {'idle' | 'loading' | 'ready' | 'error'}
 */
export function getOpenCVState() {
  return state;
}

/**
 * WASM 지원 여부 확인
 * @returns {boolean}
 */
export function isWasmSupported() {
  try {
    if (typeof WebAssembly === 'object' &&
        typeof WebAssembly.instantiate === 'function') {
      const module = new WebAssembly.Module(
        Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
      );
      return module instanceof WebAssembly.Module;
    }
  } catch (e) {
    // WASM 미지원
  }
  return false;
}

/**
 * OpenCV.js lazy load
 * @param {function} [onProgress] - 진행률 콜백 (0-100)
 * @returns {Promise<void>}
 */
export function loadOpenCV(onProgress) {
  // 이미 로드됨
  if (state === 'ready') {
    return Promise.resolve();
  }

  // 로딩 중이면 기존 프로미스 반환
  if (state === 'loading' && loadPromise) {
    return loadPromise;
  }

  state = 'loading';

  loadPromise = new Promise((resolve, reject) => {
    const TIMEOUT_MS = 30000;
    let timeoutId = null;

    // XHR로 다운로드 (진행률 추적)
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/lib/opencv.js', true);
    xhr.responseType = 'text';

    xhr.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 80);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status !== 200) {
        state = 'error';
        loadPromise = null;
        reject(new Error(`OpenCV.js 다운로드 실패 (${xhr.status})`));
        return;
      }

      if (onProgress) onProgress(85);

      // 스크립트 실행
      try {
        const script = document.createElement('script');
        script.textContent = xhr.responseText;
        document.head.appendChild(script);
      } catch (e) {
        state = 'error';
        loadPromise = null;
        reject(new Error('OpenCV.js 실행 실패'));
        return;
      }

      if (onProgress) onProgress(90);

      // cv.onRuntimeInitialized 대기
      if (typeof cv !== 'undefined') {
        // cv가 이미 함수가 아닌 객체인 경우 (이미 초기화됨)
        if (typeof cv.Mat === 'function') {
          clearTimeout(timeoutId);
          state = 'ready';
          if (onProgress) onProgress(100);
          resolve();
          return;
        }

        // cv가 Promise-like인 경우 (emscripten 최신 빌드)
        if (typeof cv.then === 'function') {
          cv.then((module) => {
            clearTimeout(timeoutId);
            window.cv = module;
            state = 'ready';
            if (onProgress) onProgress(100);
            resolve();
          });
          return;
        }

        // onRuntimeInitialized 콜백 대기
        cv['onRuntimeInitialized'] = () => {
          clearTimeout(timeoutId);
          state = 'ready';
          if (onProgress) onProgress(100);
          resolve();
        };
      } else {
        state = 'error';
        loadPromise = null;
        reject(new Error('OpenCV.js 로드 실패: cv 객체 없음'));
      }
    };

    xhr.onerror = () => {
      state = 'error';
      loadPromise = null;
      reject(new Error('OpenCV.js 다운로드 실패 (네트워크 오류)'));
    };

    // 타임아웃
    timeoutId = setTimeout(() => {
      if (state !== 'ready') {
        xhr.abort();
        state = 'error';
        loadPromise = null;
        reject(new Error('OpenCV.js 로드 시간 초과'));
      }
    }, TIMEOUT_MS);

    xhr.send();
  });

  return loadPromise;
}
